// Periods — "what did we earn last month, and is it locked in".
//
// A month OPENS on the 1st and CLOSES on the last day, Asia/Dubai (operator
// ruling 2026-08-21). db/0021 enforces that calendar shape as CHECK
// constraints, so 30/31 and February 28/29 are correct by construction and
// cannot drift with a code change.
//
// WHAT A CLOSE ACTUALLY DOES, and why this screen shows three numbers per
// asset rather than one:
//
//   M  margin measured — margin the memo pairs only MEASURED. Its value sits
//      in the trading position, so recognising it in equity DEBITS the
//      position: a reclassification within the equity side, not new money.
//   Q  already posted — profit that ALREADY moved equity through real
//      double-entry postings (a write-off against a party account, say).
//   P  = M + Q — what actually reaches equity:{entity}:retained.
//
// Showing only P would hide that most of the number is a reclassification.
// The operator has been burned once by a figure that no asset stood behind;
// this layout makes the composition unavoidable.
//
// Currency: per-asset throughout. This ledger converts nothing, anywhere — a
// blended total would be an invented FX rate.
//
// The close is IRREVERSIBLE in the ordinary sense (the ledger is append-only;
// undoing means a reversal that lives in the record forever), so the primary
// action is PREVIEW. Closing requires the preview to be clean and the month to
// have ended, and both are enforced server-side too — this screen makes the
// state legible, it is not the safety mechanism.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.26): the month reads "September 2026"
// everywhere (picker, heading, button, confirm, legs table) instead of the
// raw key; the headers are "Margin measured / Already posted / Into
// retained"; the explainer paragraphs live in "About this screen"; the
// gate list drops its ids; the confirm names the amounts, not the ledger
// product; Close is disabled while the preview is refetching.
//
// 19 Sept 2026, the phone: the months are one strip that scrolls sideways
// (a month button with its pill is wider than half a phone, so wrapping
// stacked them one per line); the three-figure table is one block per asset
// under a LedgerHead that carries the asset and its Into retained figure —
// Profit's CurrencyBlock shape — because the four-column table scrolled the
// answer column off the right edge; the Close button takes the line. lg+ is
// unchanged.
//
// 21 Oct 2026 audit (overnight clean view; operator: "I need clean view for
// operator"): a month that has ended and awaits its close no longer wears the
// running month's "Open" pill — it is "Ready to close" (warn: it is what he
// acts on), or "Close blocked" while the month on screen has a failed check;
// on the phone a chip is two lines (month, then its state) so no state word is
// cut by the strip's edge. Each fact is said once: "Still running" is the pill
// beside the title (the sentence under the table is gone); a closed month's
// state is its chip, and WHEN it closed is the line under its title (the green
// "Closed 02 Oct, 11:20." under the table, the heading pill and the "All N
// checks pass" under a closed month are gone — a failed check still shows);
// the phone block's "Into retained" total row repeated its own head; and the
// "Already in retained earnings" table leaves out a month whose posted figures
// are already on the card above. The subtitle moved into the help.
//
// 21 Oct 2026 design-system sweep: a failed check is ONE line — the Worker's first clause
// with its figures in thousands (and the minus sign), the asset in front — and what follows
// the dash, or the offset list, is behind that check's own "Details", with the one fact the
// figures need (they are in the asset's smallest unit — until the Worker's `units: 'display'`, 23 Oct
// 2026, which sends them in the asset's own units, grouped, and then there is nothing to say); which
// month is ready or blocked (chipState) is untouched. The confirm is one line, the reversal note one tap down; a read
// that failed — the months, the close — is a red line with "Try again".

import { useState, type ReactNode } from 'react';
import { errorWords, readWords } from '../lib/errors';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Lock, LockOpen, Calendar } from 'lucide-react';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { Pending, Pill, StateLine } from '../components/kit';
import { ReportTable, type ReportColumn, type ReportRow } from '../components/ReportTable';
import { LedgerHead } from '../components/Ledger';
import type { PeriodGate, PeriodPreview, PeriodsResponse } from '../lib/api';
import { booksClock, displayAsset, fmtAmount, fmtDate, fmtDay, fmtMonth } from '../lib/format';
import { sameAsset } from '../lib/naming';

/** Months from the regime start to the current one, newest first. Read off
 *  the books clock so "this month" flips at Dubai midnight, not UTC's 04:00 GST. */
function monthsSince(regimeStart: string): string[] {
  const now = booksClock() ?? { y: 2026, m: 1 };
  const parts = regimeStart.split('-').map(Number);
  const ry = parts[0] ?? 2026;
  const rm = parts[1] ?? 1;
  const out: string[] = [];
  let y = now.y;
  let m = now.m;
  // guard the loop as well as the bound: a malformed regime_start must not spin
  while ((y > ry || (y === ry && m >= rm)) && out.length < 240) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

/** The month key the API speaks (YYYY-MM) in the operator's words —
 *  "September 2026" (§6 "Month"). Mid-month noon, Dubai, so no zone edge can
 *  move it into the neighbouring month. */
function monthWords(key: string): string {
  return fmtMonth(`${key}-15T12:00:00+04:00`);
}

/** "12,340.00 AED and 102,000 INR" — the amounts a close would post. */
function amountList(assets: PeriodPreview['assets']): string {
  const words = assets.map((a) => `${fmtAmount(a.profit, a.asset)} ${displayAsset(a.asset)}`);
  if (words.length <= 1) return words[0] ?? 'nothing';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** What a month's pill says. `open` is the RUNNING month; `ready` is a month
 *  that has ended and awaits its close — what he acts on, so it is warn, not
 *  the running month's quiet pill (21 Oct 2026 audit); `blocked` is the month
 *  on screen when a check failed, so a chip never says "ready" over a box that
 *  says the close is blocked; `ended` is an ended month whose checks are not (yet)
 *  known — it awaits its close and says only that. */
type PillState = 'open' | 'ended' | 'ready' | 'blocked' | 'closed' | 'unopened';

function StatusPill({ status }: { status: PillState }) {
  if (status === 'closed')  return <Pill tone="good" icon={<Lock size={11} />}>Closed</Pill>;
  if (status === 'ended')   return <Pill tone="warn" icon={<LockOpen size={11} />}>To close</Pill>;
  if (status === 'ready')   return <Pill tone="warn" icon={<LockOpen size={11} />}>Ready to close</Pill>;
  if (status === 'blocked') return <Pill tone="warn" icon={<AlertTriangle size={11} />}>Close blocked</Pill>;
  if (status === 'open')    return <Pill icon={<LockOpen size={11} />}>Open</Pill>;
  return <Pill icon={<Calendar size={11} />}>Not started</Pill>;
}

/** THE FIRST SENTENCE OF A READ THAT FAILED, THE REST BEHIND A TAP (design-system sweep, 21 Oct 2026). The books'
 *  own words are a sentence or three ("The books did not answer. The desk Mac may be asleep…"); the line says the
 *  first, `more` the rest. */
function firstSentence(words: string): { first: string; rest: string } {
  const first = words.split(/(?<=[.!?])\s+/)[0] ?? words;
  return { first, rest: words.slice(first.length).trim() };
}

/** A READ THAT FAILED, as a line of its own — never a month with nothing in it: what failed, the first sentence of
 *  what the books said, the rest behind a tap, and the way out. */
function ReadFailed({ what, error, onRetry }: { what: string; error: unknown; onRetry: () => void }) {
  const { first, rest } = firstSentence(readWords(error));
  return (
    <StateLine
      tone="bad"
      more={rest || undefined}
      action={<button type="button" className="btn-link" onClick={onRetry}>Try again</button>}
    >
      Couldn&rsquo;t read {what}. {first}
    </StateLine>
  );
}

/** A figure in the Worker's gate sentences is a bare integer in the asset's smallest unit ("41231075",
 *  "-39821025"). In thousands, and with the minus sign the screens use (U+2212): "41,231,075", "−39,821,025".
 *  A run of four digits or more that stands alone and does not start with a zero — never a date ("2026-09"),
 *  a decimal, an identifier ("tx-0041") or a word's digits; string work, so a 19-digit figure keeps every digit. */
function groupFigures(sentence: string): string {
  return sentence.replace(/(?<![\w.,]|\w-)([-−]?)([1-9]\d{3,})(?!\w|[.,-]\d)/g, (_m, sign: string, digits: string) =>
    `${sign ? '−' : ''}${digits.replace(/\B(?=(\d{3})+$)/g, ',')}`);
}

/** The one check whose sentence is a clause and then a LIST ("unmatched offset moves: a; b; c"): the clause is the
 *  line, the list is behind the tap. Keyed on the check's own name — another check's figures are never folded away. */
const LIST_GATES: ReadonlySet<string> = new Set(['offset_fully_matched']);

/** ONE FAILED CHECK, AS A LINE. The sentence is the Worker's ("Σmeters 41231075 + offset -39821025 ≠ −Q -1410050",
 *  "net trade margin 39821025 ≠ Σ deals.spread_amount 39811025 — a margin was measured that no deal explains").
 *  The line is its FIRST CLAUSE — what does not tie — with the figures in thousands and the asset in front (the
 *  check's name ends `:AED`); what follows the dash, or the list, is behind the tap, with the one fact the
 *  figures need: they are in the asset's smallest unit (unless the Worker says `units: 'display'`: then they are in the asset's own
 *  units, already grouped, and there is nothing to add). Nothing of the sentence is dropped. */
function gateParts(g: PeriodGate): { asset: string | null; clause: string; rest: string[] } {
  const asset = g.name.includes(':') ? g.name.slice(g.name.lastIndexOf(':') + 1) : null;
  const [head = '', ...tail] = g.detail.split(' — ');
  const list = LIST_GATES.has(g.name) ? /^([^:]+):\s+(.+)$/.exec(head) : null;
  // `units: 'display'` (the Worker, 23 Oct 2026): the figures already arrive in the asset's own units, signed and grouped ("−131,068.81") —
  // a grouped number is left alone. Without it (a Worker older than that, or an asset the registry does not know: 'minor') they are the
  // smallest unit, and are grouped here.
  const figures = g.units === 'display' ? (t: string) => t : groupFigures;
  const clause = figures(list ? list[1]! : head);
  const sentence = (t: string) => {
    const c = figures(t.charAt(0).toUpperCase() + t.slice(1));
    return /[.!?]$/.test(c) ? c : `${c}.`;
  };
  return { asset, clause: clause.charAt(0).toUpperCase() + clause.slice(1), rest: [...(list ? [figures(list[2]!)] : []), ...tail.map(sentence)] };
}

function GateFailure({ gate }: { gate: PeriodGate }) {
  const { asset, clause, rest } = gateParts(gate);
  const more = [...rest, ...(asset && gate.units !== 'display' ? ['Figures are in the smallest unit of the asset.'] : [])];
  return (
    <li>
      <StateLine tone="warn" more={more.length > 0 ? more.map((t, i) => <p key={i}>{t}</p>) : undefined}>
        {asset ? `${displayAsset(asset)} · ` : ''}{clause}
      </StateLine>
    </li>
  );
}

/** The checks, in words. A gate's id is an internal name and stays off the
 *  screen; a failed check is a line — what does not tie, figures in thousands —
 *  and what the Worker said after it is behind a tap. `quiet` drops the
 *  all-clear line (a closed month: the checks passing is not news); a failed
 *  check is never dropped. */
function GateList({ gates, quiet = false }: { gates: PeriodGate[]; quiet?: boolean }) {
  const failed = gates.filter((g) => !g.ok);
  if (!gates.length) return null;
  if (!failed.length) {
    if (quiet) return null;
    return (
      <p className="flex items-start gap-1.5 text-meta text-pos">
        <CheckCircle2 size={14} className="mt-px shrink-0" />
        <span>All {gates.length} checks pass.</span>
      </p>
    );
  }
  return (
    <div className="space-y-2 rounded-lg bg-warn-bg p-3" data-gates-failed={failed.length}>
      <p className="flex items-center gap-1.5 text-body font-medium text-warn">
        <AlertTriangle size={14} className="shrink-0" /> {failed.length} check{failed.length > 1 ? 's' : ''} failed — the close is blocked
      </p>
      <ul className="space-y-1.5">
        {failed.map((g) => <GateFailure key={g.name} gate={g} />)}
      </ul>
    </div>
  );
}

type PreviewAsset = PeriodPreview['assets'][number];

/** The lg+ table's columns: the asset, then the three numbers. */
const ASSET_COLUMNS: ReportColumn[] = [
  { key: 'asset',    label: 'Asset', align: 'left' },
  { key: 'measured', label: 'Margin measured' },
  { key: 'posted',   label: 'Already posted' },
  { key: 'retained', label: 'Into retained' },
];

/** ONE rendering of an asset's three figures — the lg+ row and the phone
 *  block both read from here, so the two forms cannot disagree on a figure
 *  or its weight: the two components muted, the one that reaches retained
 *  the key figure. */
function assetCells(a: PreviewAsset): Record<'asset' | 'measured' | 'posted' | 'retained', ReactNode> {
  return {
    asset:    displayAsset(a.asset),
    measured: <span className="text-tg-hint">{fmtAmount(a.memo_margin, a.asset)}</span>,
    posted:   <span className="text-tg-hint">{fmtAmount(a.real_pnl, a.asset)}</span>,
    retained: <span className="amt-key text-body">{fmtAmount(a.profit, a.asset)}</span>,
  };
}

/** The phone's block for one asset — Profit's CurrencyBlock shape: the
 *  Ledger's block header with the asset at the left and "into retained" and
 *  its figure at the right, so the answer is on the first line; then the
 *  two components as rows. (It used to close them with the same figure as an
 *  "Into retained" total row, M + Q = P — which was the head's own figure
 *  twice in one block; 21 Oct 2026 audit: the head is the answer, the rows
 *  its two parts, and the head's figure is the key figure now.)
 *  Headless and unframed, the head at the table's own cell inset. */
function AssetBlock({ a }: { a: PreviewAsset }) {
  const cells = assetCells(a);
  return (
    <section>
      <LedgerHead
        className="px-4"
        title={displayAsset(a.asset)}
        figure={<span className="amt-key">{fmtAmount(a.profit, a.asset)}</span>}
        note="into retained"
        noteFirst
      />
      <ReportTable
        head={false}
        frame={false}
        columns={[
          { key: 'line', label: '', align: 'left', nowrap: false },
          { key: 'fig',  label: displayAsset(a.asset) },
        ]}
        rows={[
          { key: 'measured', cells: { line: 'Margin measured', fig: cells.measured } },
          { key: 'posted',   cells: { line: 'Already posted',  fig: cells.posted } },
        ]}
      />
    </section>
  );
}

export function Periods() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const periodsQ = useQuery<PeriodsResponse>({
    queryKey: ['periods'],
    queryFn: () => api.listPeriods(),
  });

  const months = periodsQ.data ? monthsSince(periodsQ.data.regime_start) : [];
  const active = selected ?? months[0] ?? null;

  const previewQ = useQuery<PeriodPreview>({
    queryKey: ['period-preview', active],
    queryFn: () => api.previewPeriod(active!),
    enabled: !!active,
  });

  // ONE OBSERVER PER MONTH, FETCHING NOTHING (`enabled: false`): the chips are drawn from each month's own read, and a
  // snapshot of the cache would not redraw them — a preview that finishes after the operator moved on, or a failed gate
  // that arrives late, must change its chip (Astra, 21 Oct, third pass). Nothing is fetched for a month he has not opened.
  const reads = useQueries({
    queries: months.map((m) => ({
      queryKey: ['period-preview', m],
      queryFn: () => api.previewPeriod(m),
      enabled: false,
    })),
  });

  const close = useMutation({
    mutationFn: () => api.closePeriod(active!),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['periods'] });
      qc.invalidateQueries({ queryKey: ['period-preview'] });
      qc.invalidateQueries({ queryKey: ['balance-sheet'] });
    },
  });

  const rowFor = (p: string) => periodsQ.data?.periods.find((x) => x.period === p);
  const statusOf = (p: string): 'open' | 'closed' | 'unopened' => rowFor(p)?.status ?? 'unopened';

  const preview = previewQ.data;
  const gatesFailed = preview?.gates.some((g) => !g.ok) ?? false;
  const activeClosed = active ? statusOf(active) === 'closed' : false;
  // Disabled while the preview is refetching (§5.26): the figures the confirm
  // names must be the ones the close will post.
  // …and never over a read that FAILED: a failed refetch leaves the last good data in `preview`, whose passing checks would
  // otherwise leave Close enabled beside "Couldn't read the close" (Astra, 21 Oct).
  // "idle" exactly: a read PAUSED by a lost connection (cached data kept, isFetching false) is not settled either (Astra, 22 Oct).
  const canClose = !!preview && preview.period_ended && !gatesFailed && !activeClosed && previewQ.fetchStatus === 'idle' && !previewQ.isError;
  const closedAt = activeClosed && preview ? rowFor(preview.period)?.closed_at ?? null : null;

  // WHAT A MONTH'S CHIP SAYS (21 Oct 2026 audit). The books' own state is open
  // or closed; an OPEN month that is not the running one has ended and awaits
  // its close. The newest month is the running one. The chip is read from THAT
  // month's own preview in the query cache — the month on screen and every month
  // read before it (Astra, 21 Oct, twice):
  //   · a check that failed stays "Close blocked" wherever the operator is looking, and through a failed refetch;
  //   · "Ready to close" only from a read that SUCCEEDED and has settled — never while it is reading again, never when
  //     the last read failed (the red line says so), never from a month whose checks were not read;
  //   · anything else — not read yet, reading, failed with nothing to show — says "To close".
  const chipState = (p: string): PillState => {
    const st = statusOf(p);
    if (st !== 'open') return st;
    if (p === months[0]) return 'open';
    const read = reads[months.indexOf(p)];
    if (!read?.data) return 'ended';
    if (read.data.gates.some((g) => !g.ok)) return 'blocked';
    // the month on screen is the one with a LIVE observer: its fetch state (previewQ) is what the screen shows — the passive
    // observer's can lag it by a render, which once let a stale month read "Ready" while it was being read again. Ready needs
    // `idle` exactly: fetching AND paused (offline, passing data cached) are both unsettled.
    const idle = (p === active ? previewQ.fetchStatus : read.fetchStatus) === 'idle';
    if (read.data.period_ended && read.status === 'success' && idle) return 'ready';
    return 'ended';
  };

  // The retained-earnings table says what each month's close posted. The month
  // on screen already prints those figures on its card, so its legs are left
  // out when they read the same — a leg that DIFFERS from the card stays, it
  // is the one place the posted figure is read.
  const shownLegs = (periodsQ.data?.legs ?? []).filter((l) => {
    if (!activeClosed || l.period !== active) return true;
    if (!preview) return !previewQ.isLoading;
    const a = preview.assets.find((x) => sameAsset(x.asset, l.asset));
    return !a || fmtAmount(a.profit, a.asset) !== fmtAmount(l.profit, l.asset);
  });
  const legs: ReportRow[] = shownLegs.map((l) => ({
    key: l.reference,
    cells: {
      month:  monthWords(l.period),
      asset:  displayAsset(l.asset),
      profit: fmtAmount(l.profit, l.asset),
    },
  }));

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="Period close"
        help={
          <>
            <p>
              Closing sweeps the month&apos;s profit into retained earnings — once, and permanently,
              after the month has ended. Months run 1st to last day, Dubai time.
            </p>
            <p>
              Margin measured is the margin the trades measured — its value sits in the trading
              position, so the close moves it into equity rather than creating it. Already posted is
              profit that moved equity when it was posted. Together they are what lands in retained
              earnings.
            </p>
            <p>Reopening a closed month needs a written reason and reverts the postings.</p>
            <p>
              Profit earned before the desk moved onto these books sits in retained earnings as a
              single derived opening entry, not as monthly closes — legacy is the record for that
              period.
            </p>
          </>
        }
      />

      {periodsQ.isError && <ReadFailed what="the months" error={periodsQ.error} onRetry={() => periodsQ.refetch()} />}

      {/* ── month picker ── */}
      {/* ON THE PHONE the months are one strip that scrolls sideways, newest
          first, edge to edge (no scrollbar drawn) — as the period chips do.
          A month button with its pill is wider than half a phone, so the
          wrapping row stacked them one per line, a column as tall as the
          regime is old, above the figures. At lg+ the row wraps as before. */}
      {/* 21 Oct 2026 audit: on the phone a chip is TWO lines — the month, then
          its state — so the second chip's state word is not cut by the strip's
          edge ("Ope…", "Clos…"); at lg+ they stand on one line as before. */}
      <section className="flex gap-2 overflow-x-auto -mx-3 px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:px-0 lg:flex-wrap lg:overflow-visible">
        {months.map((m) => {
          const on = m === active;
          return (
            <button
              key={m}
              type="button"
              onClick={() => { setSelected(m); setConfirming(false); }}
              className={`shrink-0 rounded-lg border px-2.5 py-1.5 text-left text-body ${
                on ? 'border-tg-link bg-panel-2' : 'border-stroke bg-panel'
              }`}
            >
              <span className="block lg:inline">{monthWords(m)}</span>
              <span className="mt-1 block lg:ml-2 lg:mt-0 lg:inline"><StatusPill status={chipState(m)} /></span>
            </button>
          );
        })}
      </section>

      {previewQ.isLoading && <Pending />}
      {previewQ.isError && <ReadFailed what="the close" error={previewQ.error} onRetry={() => previewQ.refetch()} />}

      {preview && (
        <section className="card space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-medium">{monthWords(preview.period)}</h2>
              {/* A closed month's state is its chip above; WHEN it closed is
                  said here, once (21 Oct 2026 audit). */}
              <p className="text-meta text-tg-hint">
                {fmtDay(preview.window.start, true)} →{' '}
                {fmtDay(new Date(preview.window.end).getTime() - 1, true)}
                {activeClosed && <> · closed{closedAt ? ` ${fmtDate(closedAt)}` : ''}</>}
              </p>
            </div>
            {!preview.period_ended && <Pill>Still running</Pill>}
          </div>

          <GateList gates={preview.gates} quiet={activeClosed} />

          {preview.assets.length === 0 ? (
            <ReportTable columns={ASSET_COLUMNS} rows={[]} empty="Nothing in this month." />
          ) : (
            <>
              {/* ── lg+: one table, the three numbers across ── */}
              <div className="hidden lg:block">
                <ReportTable
                  columns={ASSET_COLUMNS}
                  rows={preview.assets.map((a) => ({ key: a.asset, cells: assetCells(a) }))}
                  empty="Nothing in this month."
                />
              </div>

              {/* ── below lg: one block per asset ── */}
              <div className="lg:hidden space-y-4">
                {preview.assets.map((a) => <AssetBlock key={a.asset} a={a} />)}
              </div>
            </>
          )}

          {/* ── action ── a closed month has none; a running month's "Still
              running" is the pill beside its title (said once). */}
          {activeClosed || !preview.period_ended ? null : confirming ? (
            <div className="space-y-2 rounded-lg bg-warn-bg p-3">
              {/* The consequence, with the figures (§7 rule 11) — one line; that undoing leaves a
                  reversal in the record is one tap down. */}
              <StateLine tone="warn" more="Undoing it leaves a reversal in the record.">
                Close {monthWords(preview.period)}? Posts {amountList(preview.assets)} into retained earnings.
              </StateLine>
              {/* On the phone the gold button takes the line and Cancel keeps
                  its own width beside it; at lg+ both sit at their own width. */}
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-primary flex-1 lg:flex-none px-4 py-2 text-body"
                  disabled={close.isPending || !canClose}
                  onClick={() => close.mutate()}
                >
                  {close.isPending ? 'Closing…' : `Close ${monthWords(preview.period)}`}
                </button>
                <button type="button" className="btn-secondary shrink-0 px-4 py-2 text-body" onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn-primary w-full lg:w-auto px-4 py-2 text-body disabled:opacity-50"
              disabled={!canClose}
              onClick={() => setConfirming(true)}
            >
              Close {monthWords(preview.period)}
            </button>
          )}

          {close.isError && (
            /* words:ok — a close the books refused or could not confirm is read whole: it says whether the month was sealed */
            <StateLine tone="bad">{errorWords(close.error)}</StateLine>
          )}
        </section>
      )}

      {/* ── what has already been swept ── */}
      {legs.length > 0 && (
        <section>
          <h2 className="mb-2 font-medium">Already in retained earnings</h2>
          <ReportTable
            columns={[
              { key: 'month',  label: 'Month', align: 'left' },
              { key: 'asset',  label: 'Asset', align: 'left' },
              { key: 'profit', label: 'Profit' },
            ]}
            rows={legs}
          />
        </section>
      )}
    </div>
  );
}
